import {
  API_BASE_URL,
  type ConfigurationsListResponse,
  type ConfigurationResponse,
  type UpdateConfigurationResponse,
  type DeleteConfigurationResponse,
  type CreateConfigurationRequest,
  type UpdateConfigurationRequest,
  type GetConfigurationsParams,
  type GetResearchConfigurationsLiteParams,
  type ResearchConfigurationsLiteResponse,
} from '@types';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { parseConfiguration } from '@utils';
import {
  applyExposureConfigTranslation,
  translateExposureEvents,
} from '../utils/translateExposureConfig';

type ConfigurationApiRecord = Record<string, unknown>;

/**
 * GET responses may expose exposure events as `exposure_events` (DB/API snake_case).
 * The webapp model uses `exposureEvents` (camelCase).
 */
function withNormalizedExposureEvents(data: unknown): Record<string, unknown> {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  const record: ConfigurationApiRecord = {
    ...(data as ConfigurationApiRecord),
  };
  const camel = record.exposureEvents;
  const snake = record.exposure_events;
  const camelLen = Array.isArray(camel) ? camel.length : 0;
  const snakeList = Array.isArray(snake) ? snake : null;
  if (camelLen === 0 && snakeList !== null && snakeList.length > 0) {
    record.exposureEvents = snakeList;
  }
  if (Array.isArray(record.exposureEvents)) {
    record.exposureEvents = translateExposureEvents(
      record.exposureEvents as unknown[]
    );
  }
  return record;
}

// ============================================================================
// API FUNCTIONS - Configurations
// ============================================================================

/**
 * Validates pagination parameters
 */
const validatePaginationParams = (page?: number, size?: number): void => {
  if (page !== undefined && page < 1) {
    throw new Error('Page must be >= 1');
  }
  if (size !== undefined) {
    if (size < 1) {
      throw new Error('Size must be >= 1');
    }
    if (size > 100) {
      throw new Error('Size must be <= 100');
    }
  }
};

/**
 * Builds query string from parameters
 */
const buildQueryString = (params: GetConfigurationsParams): string => {
  const queryParams: string[] = [];

  if (params.status) {
    queryParams.push(`status=${encodeURIComponent(params.status)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.size !== undefined) {
    queryParams.push(`size=${params.size}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

const buildResearchLiteQueryString = (
  params: GetResearchConfigurationsLiteParams
): string => {
  const queryParams: string[] = [];

  if (params.status) {
    queryParams.push(`status=${encodeURIComponent(params.status)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.size !== undefined) {
    queryParams.push(`size=${params.size}`);
  }
  if (params.searchId?.trim()) {
    queryParams.push(`searchId=${encodeURIComponent(params.searchId.trim())}`);
  }
  if (params.searchPatient?.trim()) {
    queryParams.push(
      `searchPatient=${encodeURIComponent(params.searchPatient.trim())}`
    );
  }
  if (params.searchDate?.trim()) {
    queryParams.push(
      `searchDate=${encodeURIComponent(params.searchDate.trim())}`
    );
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

/**
 * List configurations with pagination and filtering
 */
export const getConfigurations = async (
  params?: GetConfigurationsParams
): Promise<ConfigurationsListResponse> => {
  const page = params?.page ?? 1;
  const size = params?.size ?? 20;

  validatePaginationParams(page, size);

  const queryString = buildQueryString({ ...params, page, size });
  const url = `${API_BASE_URL}/services/v1/configurations${queryString}`;

  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getUserConfigurationsLite = async (
  userId: number | string,
  params?: GetResearchConfigurationsLiteParams
): Promise<ResearchConfigurationsLiteResponse> => {
  const page = params?.page ?? 1;
  const size = params?.size ?? 25;

  validatePaginationParams(page, size);

  const queryString = buildResearchLiteQueryString({ ...params, page, size });
  const response = await fetch(
    `${API_BASE_URL}/services/v1/users/${encodeURIComponent(String(userId))}/configurations-lite${queryString}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

/**
 * Get a single configuration by ID
 */
export const getConfiguration = async (
  id: number | string
): Promise<ConfigurationResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/${id}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  const responseData = await response.json();
  const configString = responseData.data?.config ?? responseData.config;
  const config = parseConfiguration(configString);
  const payload = responseData.data ?? responseData;
  const normalized = withNormalizedExposureEvents(payload);
  const type = (normalized as { type?: string }).type;
  const translatedConfig =
    type === 'exposure' &&
    config &&
    typeof config === 'object' &&
    !Array.isArray(config)
      ? applyExposureConfigTranslation(config as Record<string, unknown>)
      : config;

  return {
    data: {
      ...normalized,
      config: translatedConfig || {},
    } as DeviceConfigurationApi,
  };
};

/**
 * Create a new configuration
 */
export const createConfiguration = async (
  data: CreateConfigurationRequest
): Promise<ConfigurationResponse> => {
  const response = await fetch(`${API_BASE_URL}/services/v1/configurations`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const res = await response.json();
  // API may return { data: {...} } or the configuration object at the top level
  const responseData = res?.data ?? res;
  if (!responseData || typeof responseData !== 'object') {
    throw new Error('Invalid create configuration response');
  }
  const config = parseConfiguration(
    (responseData as { config?: unknown }).config
  );
  return {
    data: {
      ...(responseData as object),
      config: config || {},
    } as ConfigurationResponse['data'],
  };
};

/**
 * Update an existing configuration (partial update)
 */
export const updateConfiguration = async (
  id: number | string,
  data: UpdateConfigurationRequest
): Promise<UpdateConfigurationResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/${id}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const repairConfigurationHrData = async (
  id: number | string
): Promise<
  | {
      configurationId: number;
      repairedEvents: unknown[];
      repairedOnly: unknown[];
      originalCount: number;
      repairedCount: number;
      duplicateMerges?: number;
      gapFillCount?: number;
    }
  | {
      status: 'no-op';
      message: string;
      configurationId: number;
      repairedOnly: unknown[];
      originalCount: number;
      repairedCount: number;
      duplicateMerges: number;
      gapFillCount: number;
    }
> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/${id}/repair-hr-data`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

/**
 * Delete a configuration by ID
 */
export const deleteConfiguration = async (
  id: number | string
): Promise<DeleteConfigurationResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/${id}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};
